import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';

import Button from '../fields/Button';
import Fade from '../transitions/Fade';
import Type from '../Typography';

import Close from './Close';
import styles from './Toast.less';
import { call } from '../utils';

const TOAST_CONTAINER = styles.container;

const cx = classnames.bind(styles);

/**
 * Show a toast message.
 *
 * **Access:**
 * ```js
 * import {Toast} from '@iss/client-react-components'
 * ```
 * To get a consistent fadeout animation, you will want to always render the Toast component and set the show prop true/false to display/dismiss.
 *
 * @class Toast
 * @component
 */
export default class Toast extends React.Component {
  static propTypes = {
    /** The content of the toast message */
    children: PropTypes.any,
    /** A callback to remove the toast */
    onDismiss: PropTypes.func,
    /** A callback to do any cleanup after the Toast has been hidden. */
    onDismissed: PropTypes.func,
    /** An additional action to perform (such as a button to undo) */
    action: PropTypes.node,
    /** show/hide the toast */
    show: PropTypes.bool,
  };

  static defaultProps = {
    show: true,
  };

  static getDerivedStateFromProps(props, state) {
    /* istanbul ignore next */
    return 'show' in props ? { ...state, out: !props.show } : null;
  }

  state = {};

  get mountPoint() {
    if (!this._el) {
      const root = getContainer();
      this._el = root.appendChild(document.createElement('div'));
    }
    return this._el;
  }

  get isMounted() {
    return !!this.mountPoint.firstChild;
  }

  get currentHeight() {
    const el = this.mountPoint.firstChild;
    return el ? /* istanbul ignore next */ el.offsetHeight : 0;
  }

  componentWillUnmount() {
    this.mountPoint.remove();
  }

  onDismiss = () => {
    this.setState({ out: true });
    call(this.props.onDismiss);
  };

  onDismissed = () => {
    call(this.props.onDismissed);
  };

  render() {
    const {
      state: { out },
      props: { action, className, children, onDismiss, show, ...props },
    } = this;

    if (!show && !this.isMounted) {
      return null;
    }

    let extraControl = null;
    if (action) {
      extraControl = React.cloneElement(action, {
        className: cx(action.props.className, 'action'),
        ...(action.type === Button ? { kind: 'link' } : null),
      });
    }

    const content = (
      <Fade appear in={!out} onExited={this.onDismissed} direction="up">
        <Type className={cx('root', className)} {...props} style={{ '--height': `${this.currentHeight}px` }}>
          <span className={cx('content')}>{children}</span>
          <span className={cx('controls')}>
            {extraControl}
            <Close onClick={this.onDismiss} />
          </span>
        </Type>
      </Fade>
    );

    return ReactDOM.createPortal(content, this.mountPoint);
  }
}

function getContainer() {
  let div = document.getElementById(TOAST_CONTAINER);
  if (!div) {
    div = document.createElement('div');
    div.setAttribute('id', TOAST_CONTAINER);
    div.setAttribute('class', cx('container'));
  }

  //keep moving the container to the end of the body
  if (div.nextSibling || !div.parentNode) {
    document.body.appendChild(div);
  }
  return div;
}
